import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ArrowLeft, Save } from "lucide-react";
import {
  createTaluka,
  updateTaluka,
  fetchTalukaById,
  resetTalukaStatuses,
} from "../../store/slices/talukasSlice";
import { fetchDistricts } from "../../store/slices/districtsSlice";
import { TextField, CheckboxField } from "../../components/ui/FormControls";
import { showApiError, showApiSuccess } from "../../utils/toast";
import Loader from "../../components/Loader";

const talukaSchema = yup.object({
  district_uuid: yup.string().required("District is required"),
  name: yup
    .string()
    .required("Taluka name is required")
    .max(255, "Taluka name must be less than 255 characters"),
  status: yup.boolean(),
});

export default function AddTalukaPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const editUuid = searchParams.get("edit");

  const {
    taluka,
    talukaCreateStatus,
    talukaUpdateStatus,
    talukaFetchStatus,
    loading,
  } = useSelector((state) => state.talukas);

  const { districts = [] } = useSelector((state) => state.districts);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(talukaSchema),
    defaultValues: {
      district_uuid: "",
      name: "",
      status: true,
    },
  });

  useEffect(() => {
    dispatch(fetchDistricts({}));
  }, [dispatch]);

  useEffect(() => {
    if (editUuid) {
      dispatch(fetchTalukaById(editUuid));
    }
  }, [editUuid, dispatch]);

  useEffect(() => {
    if (editUuid && taluka && talukaFetchStatus === "success") {
      reset({
        district_uuid: taluka.district?.uuid || "",
        name: taluka.name || "",
        status: Boolean(taluka.status),
      });
    }
  }, [taluka, talukaFetchStatus, editUuid, reset]);

  const onSubmit = async (formData) => {
    try {
      const payload = {
        district_uuid: formData.district_uuid,
        name: formData.name,
        status: formData.status,
      };

      if (editUuid) {
        await dispatch(updateTaluka({ uuid: editUuid, data: payload })).unwrap();
        showApiSuccess("Taluka updated successfully");
      } else {
        await dispatch(createTaluka(payload)).unwrap();
        showApiSuccess("Taluka created successfully");
      }

      navigate("/talukas");
    } catch (error) {
      showApiError(null, error || `Failed to ${editUuid ? "update" : "create"} taluka`);
    }
  };

  useEffect(() => {
    return () => {
      dispatch(resetTalukaStatuses());
    };
  }, [dispatch]);

  const isSubmitting =
    talukaCreateStatus === "loading" || talukaUpdateStatus === "loading";

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {loading && <Loader />}

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate("/talukas")}
              className="text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {editUuid ? "Edit Taluka" : "Add New Taluka"}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {editUuid
                  ? "Update taluka information"
                  : "Create a new taluka linked to a district"}
              </p>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Taluka Information
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                District <span className="text-red-500">*</span>
              </label>
              <select
                {...register("district_uuid")}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a district</option>
                {districts.map((district) => (
                  <option key={district.uuid} value={district.uuid}>
                    {district.name} ({district.state?.name || "N/A"})
                  </option>
                ))}
              </select>
              {errors?.district_uuid && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.district_uuid.message}
                </p>
              )}
            </div>

            <div>
              <TextField
                label="Taluka Name"
                required
                placeholder="Enter taluka name"
                {...register("name")}
                error={errors?.name?.message}
              />
            </div>

            <div>
              <Controller
                control={control}
                name="status"
                render={({ field }) => (
                  <CheckboxField
                    label="Active Status"
                    checked={Boolean(field.value)}
                    onChange={(checked) => field.onChange(checked)}
                  />
                )}
              />
              <p className="text-sm text-gray-500 mt-1">
                Enable this taluka to make it available for selection
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate("/talukas")}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="h-4 w-4" />
              <span>
                {isSubmitting
                  ? "Saving..."
                  : editUuid
                  ? "Update Taluka"
                  : "Create Taluka"}
              </span>
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
